import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getTournament,
  addPlayer,
  updatePlayerStatus,
  updateTournament,
  updatePlayerOrder,
  startTournament,
  subscribePrivateTournamentStream,
} from '../api/tournamentsApi';
import { getEvent } from '../api/eventsApi';
import useEventMembers from '../hooks/useEventMembers';
import { Button, Spinner, PageError, AlertModal } from '../components/UI';
import { useToast } from '../components/Toast';
import useRefreshOnVisible from '../hooks/useRefreshOnVisible';
import { useFeedbackScreen } from '../utils/feedbackScreen';
import { StatusBadge } from '../components/tournament/TournamentBadges';
import { IconArrowLeft, IconTrophy } from '../components/tournament/TournamentIcons';
import { FORMAT_LABELS, SPORT_LABELS, formatSupportsSeeding } from '../components/tournament/tournamentConstants';
import DraftSettings from '../components/tournament/DraftSettings';
import TournamentMemberList from '../components/tournament/TournamentMemberList';
import TournamentTimer from '../components/tournament/TournamentTimer';
import TournamentEditView from '../components/tournament/TournamentEditView';
import RunningView from '../components/tournament/RunningView';

// Задержка переподключения SSE при ошибке (мс)
const SSE_RECONNECT_DELAY_MS = 3_000;

// organizationMemberId активных игроков, упорядоченные по seed (null — в конец, тай-брейк по id).
// Используется как стартовый «предпочтительный порядок» для ручного посева.
const activeMemberIdsBySeed = (players) => (players || [])
  .filter(p => p.status === 'ACTIVE')
  .slice()
  .sort((a, b) => {
    const sa = a.seed ?? Number.MAX_SAFE_INTEGER;
    const sb = b.seed ?? Number.MAX_SAFE_INTEGER;
    return sa !== sb ? sa - sb : (a.id ?? 0) - (b.id ?? 0);
  })
  .map(p => p.organizationMemberId);

/**
 * Умный мёрж SSE-снапшота с локальным стейтом.
 * Если editingMatchId задан — не трогаем этот матч (организатор сейчас его редактирует).
 * Это предотвращает сброс открытого ScoreModal при обновлениях от других организаторов.
 */
function mergeTournamentSnapshot(prev, incoming, editingMatchId) {
  if (!editingMatchId || !prev?.rounds) return incoming;

  const updatedRounds = incoming.rounds.map(incomingRound => {
    const prevRound = prev.rounds.find(r => r.id === incomingRound.id);
    return {
      ...incomingRound,
      matches: incomingRound.matches.map(incomingMatch => {
        if (incomingMatch.id === editingMatchId) {
          // Матч открыт в ScoreModal — берём локальную версию, не трогаем
          const localMatch = prevRound?.matches?.find(m => m.id === editingMatchId);
          return localMatch || incomingMatch;
        }
        return incomingMatch;
      }),
    };
  });

  return { ...incoming, rounds: updatedRounds };
}

const TournamentPage = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [tournament, setTournament] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [pageError, setPageError]   = useState(null);
  // Лидерборд из последнего SSE-снапшота — передаётся в RunningView.
  // Нужен потому что updateMatchStatus не трогает tournament.updatedAt,
  // и RunningView иначе не узнает что надо перезапросить лидерборд.
  const [sseLeaderboard, setSseLeaderboard] = useState(null);

  // SSE-уведомления: null или { title, message, icon, onConfirm }
  // Показываются в AlertModal — требуют явного подтверждения от пользователя.
  const [sseAlert, setSseAlert] = useState(null);

  const [startLoading, setStartLoading]   = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [editMode, setEditMode]           = useState(false); // режим редактирования RUNNING-турнира

  // Уникальный идентификатор этой сессии страницы — отправляется в X-Client-Id.
  // Бэкенд прокидывает его в SSE-снапшот как initiatorClientId, что позволяет
  // отличить собственные изменения от чужих и не показывать лишние алерты.
  const clientId = useRef(crypto.randomUUID());

  // SSE для синхронизации между несколькими организаторами
  const esRef            = useRef(null);
  const reconnectTimer   = useRef(null);
  // Последний полученный снапшот — используется при закрытии ScoreModal (cancel/409),
  // чтобы восстановить редактируемый матч до актуального состояния без лишнего запроса
  const lastSnapshotRef  = useRef(null);
  // id матча открытого в ScoreModal прямо сейчас — для умного мёржа снапшота.
  // Хранится в ref (а не в state) чтобы SSE-коллбэк видел актуальное значение
  // без пересоздания EventSource при каждом открытии/закрытии модала.
  const editingMatchIdRef   = useRef(null);
  // Колбэк принудительного закрытия ScoreModal — передаётся из MatchRow при открытии.
  // При SSE-конфликте на редактируемый матч вызываем его, чтобы закрыть модал без сохранения.
  const forceCloseModalRef  = useRef(null);
  // Актуальный tournament в ref — читается из замыканий SSE и visibilitychange
  // без необходимости пересоздавать коллбэки при каждом обновлении стейта.
  const tournamentRef = useRef(null);
  tournamentRef.current = tournament;

  // Объединённый список участников для DRAFT (турнирные + новые из события)

  // ── Черновик: локальное состояние настроек ─────────────────────────────────
  // Инициализируются в useEffect когда турнир загружен
  const [draftCourts,   setDraftCourts]   = useState('');
  const [draftScoring,  setDraftScoring]  = useState('POINTS');
  const [draftDuration, setDraftDuration] = useState(15);
  const [draftRanking,  setDraftRanking]  = useState('POINTS');

  // Локальные статусы участников: organizationMemberId → 'ACTIVE' | 'REMOVED'
  // undefined = не изменялся, используем tournament.players как источник правды
  const [localStatuses, setLocalStatuses] = useState({});

  // Посев (random / manual order) — черновое состояние, применяется при сохранении.
  // draftRandomOrder: true — случайный (дефолт), false — ручной.
  // draftOrder: предпочтительный порядок активных игроков (organizationMemberId);
  //   итоговый список пересекается с текущим активным составом, поэтому тумблеры
  //   состава не требуют ручной правки этого массива.
  const [draftRandomOrder, setDraftRandomOrder] = useState(true);
  const [draftOrder, setDraftOrder]             = useState([]);

  const fetchTournament = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setPageError(null);
    try {
      const data = await getTournament(publicId);
      setTournament(data);
    } catch (err) {
      if (!silent) setPageError(err.message || 'Не удалось загрузить турнир');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [publicId]);

  useEffect(() => { fetchTournament(); }, [fetchTournament]);

  // organizationId события — нужен AddParticipantPanel внутри TournamentMemberList
  // (поиск участников организации при добавлении нового человека прямо со страницы
  // турнира). Без бэкенд-правок: просто переиспользуем getEvent, который уже отдаёт
  // это поле (как в ViewEventPage). Один раз на eventPublicId.
  const [organizationId, setOrganizationId] = useState(null);
  useEffect(() => {
    if (!tournament?.eventPublicId) return;
    getEvent(tournament.eventPublicId)
      .then(ev => setOrganizationId(ev?.organizationId ?? null))
      .catch(() => {});
  }, [tournament?.eventPublicId]);

  // Тихо обновляем турнир при возврате на свёрнутую страницу (данные могли устареть).
  // silent=true не сбрасывает локальные правки черновика (init-эффект завязан на publicId).
  const refreshOnVisible = useCallback(() => fetchTournament({ silent: true }), [fetchTournament]);
  useRefreshOnVisible(refreshOnVisible);

  // ── SSE: синхронизация между несколькими организаторами ───────────────────

  const connectSSE = useCallback(() => {
    if (esRef.current) esRef.current.close();

    esRef.current = subscribePrivateTournamentStream(
      publicId,
      // onUpdate: пришёл снапшот турнира по SSE
      ({ tournament: incoming, leaderboard: incomingLeaderboard, initiatorClientId }) => {
        if (!incoming) return;
        lastSnapshotRef.current = incoming;
        // Обновляем лидерборд из SSE — он уже пересчитан бэкендом.
        // Это покрывает случай очистки счёта: updateMatchStatus не трогает
        // tournament.updatedAt, поэтому RunningView иначе не перезапросит.
        if (incomingLeaderboard) setSseLeaderboard(incomingLeaderboard);

        // Наше собственное изменение (или наша же базовая синхронизация при подписке —
        // бэк метит начальный снапшот нашим clientId) — алерты не нужны, UI обновится сам.
        const isOurChange = initiatorClientId && initiatorClientId === clientId.current;

        if (!isOurChange) {
          const current = tournamentRef.current;

          // Если ScoreModal открыт и именно этот матч изменил другой организатор —
          // показываем алерт; при подтверждении закрываем модал без сохранения.
          if (editingMatchIdRef.current && current) {
            const incomingMatch = incoming.rounds
              ?.flatMap(r => r.matches || [])
              .find(m => m.id === editingMatchIdRef.current);
            const localMatch = current.rounds
              ?.flatMap(r => r.matches || [])
              .find(m => m.id === editingMatchIdRef.current);
            if (incomingMatch && localMatch && incomingMatch.version !== localMatch.version) {
              setSseAlert({
                icon: '⚠️',
                title: 'Матч уже сохранён',
                message: 'Другой организатор сохранил счёт этого матча. Ваши изменения отменены — данные обновлены.',
                onConfirm: () => forceCloseModalRef.current?.(),
              });
            }
          }

          // Если изменились ключевые настройки турнира — уведомляем.
          if (current) {
            // Старт/сброс таймера другим организатором — не «настройка»: таймер-бар сам
            // подхватит новое timerStartedAt из снапшота. Модалку в этом случае не
            // показываем (тихое обновление, как при вводе счёта). Таймер-снапшот
            // распознаём по изменившемуся timerStartedAt — отдельное действие приходит
            // отдельным снапшотом, поэтому смена настроек и таймера не пересекаются.
            const timerChanged = incoming.timerStartedAt !== current.timerStartedAt;
            // Любое изменение настроек отражается в updatedAt — надёжнее чем перечислять поля.
            const settingsChanged = incoming.updatedAt !== current.updatedAt;
            if (settingsChanged && !timerChanged) {
              setSseAlert({
                icon: '🔄',
                title: 'Настройки турнира изменены',
                message: 'Другой организатор изменил настройки турнира. Страница обновлена.',
              });
            }
          }
        }

        // Используем ref вместо state — коллбэк не пересоздаётся при открытии модала,
        // поэтому editingMatchId через замыкание всегда был бы null
        setTournament(prev => mergeTournamentSnapshot(prev, incoming, editingMatchIdRef.current));
      },
      // onError: SSE-соединение упало, пробуем переподключиться через 3 сек.
      // Если страница скрыта — не ставим таймер: восстановим при visibilitychange.
      () => {
        esRef.current?.close();
        esRef.current = null;
        if (document.visibilityState === 'hidden') return;
        reconnectTimer.current = setTimeout(connectSSE, SSE_RECONNECT_DELAY_MS);
      },
      // clientId — бэк пометит им начальный снапшот, чтобы своя базовая
      // синхронизация не считалась чужой правкой (isOurChange).
      clientId.current,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId]); // editingMatchId намеренно вынесен: не пересоздаём коннект при открытии модала

  // Подключаем SSE только для RUNNING-турнира (в DRAFT матчей нет, нечего синхронизировать)
  useEffect(() => {
    if (!tournament || tournament.status !== 'RUNNING' || !tournament.canManage) return;
    connectSSE();
    return () => {
      clearTimeout(reconnectTimer.current);
      esRef.current?.close();
      esRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament?.status, publicId]);

  // При возврате на видимую страницу — переподключаем SSE если соединение упало в фоне.
  // Работает в паре с onError выше: тот не ставит таймер когда страница скрыта.
  // Если турнир был FINISHED → стал RUNNING пока мы были в фоне: useRefreshOnVisible
  // вызовет fetchTournament → tournament.status обновится → useEffect выше подключит SSE.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const t = tournamentRef.current;
      if (t?.status === 'RUNNING' && t?.canManage !== false && !esRef.current) {
        connectSSE();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [connectSSE]);

  // Поллинг при FINISHED — раз в 15 сек проверяем статус на случай возврата в работу.
  // Как только status снова станет RUNNING, useEffect выше подключит SSE.
  useEffect(() => {
    if (tournament?.status !== 'FINISHED') return;
    const interval = setInterval(() => fetchTournament({ silent: true }), 15_000);
    return () => clearInterval(interval);
  }, [tournament?.status, fetchTournament]);

  // Коллбэки для MatchRow → синхронизируем editingMatchId + ref
  const handleScoreModalOpen = useCallback((matchId, forceClose) => {
    editingMatchIdRef.current  = matchId;
    forceCloseModalRef.current = forceClose ?? null;
  }, []);

  const handleScoreModalClose = useCallback((matchId) => {
    editingMatchIdRef.current  = null;
    forceCloseModalRef.current = null;
    // При закрытии (cancel или 409) берём матч из последнего снапшота —
    // за время пока модал был открыт другой организатор мог изменить этот матч.
    // Если снапшота нет (SSE ещё не приходил) — делаем тихий рефетч.
    if (lastSnapshotRef.current) {
      const freshMatch = lastSnapshotRef.current.rounds
        ?.flatMap(r => r.matches || [])
        .find(m => m.id === matchId);
      if (freshMatch) {
        setTournament(prev => {
          if (!prev?.rounds) return prev;
          const newRounds = prev.rounds.map(round => ({
            ...round,
            matches: round.matches.map(m => m.id === matchId ? freshMatch : m),
          }));
          return { ...prev, rounds: newRounds };
        });
      }
    } else {
      fetchTournament({ silent: true });
    }
  }, [fetchTournament]);

  // Инициализируем локальные настройки черновика при загрузке турнира.
  // Зависимость — publicId турнира, чтобы не сбрасывать локальные изменения при тихом рефетче.
  useEffect(() => {
    if (!tournament) return;
    setDraftCourts(tournament.courtsCount ?? '');
    setDraftScoring(tournament.matchScoring ?? 'POINTS');
    setDraftDuration(tournament.matchDurationMinutes ?? 15);
    setDraftRanking(tournament.rankingMode ?? 'POINTS');
    const map = {};
    (tournament.players || []).forEach(p => {
      map[p.organizationMemberId] = p.status;
    });
    setLocalStatuses(map);
    setDraftRandomOrder(tournament.randomSeeding !== false); // null/true → случайный
    setDraftOrder(activeMemberIdsBySeed(tournament.players));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament?.publicId]); // только при смене турнира, не при каждом тихом обновлении

  // Объединённый список участников: загружаем только для DRAFT.
  const { members: eventMembers, loading: membersLoading, refetch: refetchEventMembers } = useEventMembers(
    tournament?.eventPublicId,
    tournament?.players,
    /* skip= */ !tournament || tournament.status !== 'DRAFT',
  );

  // ── Черновик: локальный тумблер участника (без API) ───────────────────────

  const draftIsActive = (memberId) => {
    const status = localStatuses[memberId];
    if (status !== undefined) return status === 'ACTIVE';
    const player = (tournament?.players || []).find(p => p.organizationMemberId === memberId);
    return player?.status === 'ACTIVE';
  };

  const toggleMemberLocal = (memberId) => {
    const current = draftIsActive(memberId);
    setLocalStatuses(prev => ({ ...prev, [memberId]: current ? 'REMOVED' : 'ACTIVE' }));
  };

  // ── Посев: производные значения чернового порядка ──────────────────────────
  // seedOrderItems — активные участники в черновом порядке (пересечение
  // предпочтительного draftOrder с текущим активным составом; новые активные
  // добавляются в конец). currentActiveOrder/savedActiveOrder — для расчёта isDirty.
  const seedingSupported = formatSupportsSeeding(tournament?.format);
  const draftActiveMembers = eventMembers.filter(m => draftIsActive(m.organizationMemberId));
  const seedOrderItems = [
    ...draftOrder
      .map(id => draftActiveMembers.find(m => m.organizationMemberId === id))
      .filter(Boolean),
    ...draftActiveMembers.filter(m => !draftOrder.includes(m.organizationMemberId)),
  ];
  const currentActiveOrder = seedOrderItems.map(m => m.organizationMemberId);
  const savedActiveOrder   = activeMemberIdsBySeed(tournament?.players);

  // ── isDirty ────────────────────────────────────────────────────────────────

  const settingsDirty = tournament && (
    Number(draftCourts)   !== tournament.courtsCount                          ||
    draftScoring          !== tournament.matchScoring                         ||
    draftRanking          !== (tournament.rankingMode ?? 'POINTS')            ||
    (draftScoring === 'TIME' && Number(draftDuration) !== tournament.matchDurationMinutes)
  );

  const playersDirty = Object.entries(localStatuses).some(([memberId, status]) => {
    const original = (tournament?.players || []).find(
      p => p.organizationMemberId === Number(memberId)
    );
    if (!original) return status === 'ACTIVE'; // новый участник отмечен как активный
    return original.status !== status;
  });

  // Тумблер случайный/ручной изменён, либо ручной порядок отличается от сохранённого.
  const randomOrderDirty = seedingSupported
    && draftRandomOrder !== (tournament?.randomSeeding !== false);
  const orderDirty = seedingSupported && !draftRandomOrder
    && currentActiveOrder.join(',') !== savedActiveOrder.join(',');

  const isDirty = !!settingsDirty || playersDirty || randomOrderDirty || orderDirty;

  // ── Обновление черновика (настройки + состав) ──────────────────────────────

  const doUpdateDraft = async () => {
    await updateTournament(publicId, {
      courtsCount: Number(draftCourts),
      matchScoring: draftScoring,
      rankingMode: draftRanking,
      ...(draftScoring === 'TIME'
        ? { matchDurationMinutes: Number(draftDuration) }
        : { matchDurationMinutes: null }),
      // Режим посева применяем сразу; ручной порядок — отдельным вызовом ниже
      // (после синка состава, чтобы все активные игроки уже существовали).
      ...(seedingSupported ? { randomSeeding: draftRandomOrder } : {}),
    });

    // Синхронизируем состав, если менялись тумблеры участников
    if (playersDirty) {
      for (const member of eventMembers) {
        const desired  = draftIsActive(member.organizationMemberId) ? 'ACTIVE' : 'REMOVED';
        const existing = (tournament.players || []).find(
          p => p.organizationMemberId === member.organizationMemberId
        );
        if (!existing) {
          if (desired === 'ACTIVE') {
            await addPlayer(publicId, { organizationMemberId: member.organizationMemberId });
          }
        } else if (existing.status !== desired) {
          await updatePlayerStatus(publicId, existing.id, { status: desired });
        }
      }
    }

    // Ручной порядок: проставляем seed по текущему черновому порядку активных игроков.
    // organizationMemberId стабилен — состав уже синхронизирован выше.
    if (seedingSupported && !draftRandomOrder && currentActiveOrder.length > 0) {
      await updatePlayerOrder(publicId, currentActiveOrder);
    }
  };

  const resetDraftStateFrom = (t) => {
    setDraftCourts(t.courtsCount ?? '');
    setDraftScoring(t.matchScoring ?? 'POINTS');
    setDraftDuration(t.matchDurationMinutes ?? 15);
    setDraftRanking(t.rankingMode ?? 'POINTS');
    const map = {};
    (t.players || []).forEach(p => { map[p.organizationMemberId] = p.status; });
    setLocalStatuses(map);
    setDraftRandomOrder(t.randomSeeding !== false);
    setDraftOrder(activeMemberIdsBySeed(t.players));
  };

  const handleUpdateDraft = async () => {
    setUpdateLoading(true);
    try {
      await doUpdateDraft();
      const fresh = await getTournament(publicId);
      setTournament(fresh);
      resetDraftStateFrom(fresh);
      addToast('Черновик обновлён', 'success');
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleStart = async () => {
    setStartLoading(true);
    try {
      if (isDirty) {
        await doUpdateDraft();
      }
      const updated = await startTournament(publicId);
      setTournament(updated);
      addToast('Турнир запущен!', 'success');
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setStartLoading(false);
    }
  };

  // Посев применяется при сохранении черновика (см. doUpdateDraft), а не сразу —
  // тумблер и перетаскивание меняют только локальное состояние (draftRandomOrder/draftOrder).
  const handleReorderPlayers = (orderedItems) =>
    setDraftOrder(orderedItems.map(m => m.organizationMemberId));

  const handleTournamentUpdate = useCallback((updated) => {
    if (updated) {
      setTournament(updated);
    } else {
      fetchTournament({ silent: true });
    }
  }, [fetchTournament]);

  const handleMatchSaved = useCallback((updatedMatch) => {
    setTournament(prev => {
      if (!prev?.rounds) return prev;
      const rIdx = prev.rounds.findIndex(r =>
        (r.matches || []).some(m => m.id === updatedMatch.id)
      );
      if (rIdx === -1) return prev;

      const newMatches = prev.rounds[rIdx].matches.map(m =>
        m.id === updatedMatch.id ? updatedMatch : m
      );
      const allFinished = newMatches.every(m => m.status === 'FINISHED');

      const newRounds = prev.rounds.map((r, i) => {
        if (i !== rIdx) return r;
        return {
          ...r,
          matches: newMatches,
          // Статус раунда обновляется локально (если все матчи FINISHED → раунд становится FINISHED)
          status: allFinished ? 'FINISHED'
            : (r.status === 'FINISHED' ? 'IN_PROGRESS' : r.status),
        };
      });
      return { ...prev, rounds: newRounds };
    });

    // Ждём завершения анимации сворачивания (~600мс) + буфер, затем скролл к этому раунду
    // Причина: если рефетч сработает раньше setOpen(false), useEffect-cleanup отменит таймер
    setTimeout(() => fetchTournament({ silent: true }), 650);

    // Автоскрол убран намеренно
  }, [fetchTournament]);

  // Ярлык экрана для контекста обратной связи: URL турнира одинаковый, но UI зависит
  // от статуса (DRAFT/RUNNING/FINISHED/CANCELLED) и режима редактирования.
  const TOURNAMENT_STATUS_RU = {
    DRAFT: 'черновик', RUNNING: 'идёт', FINISHED: 'завершён', CANCELLED: 'отменён',
  };
  useFeedbackScreen(
    tournament
      ? `Турнир · ${TOURNAMENT_STATUS_RU[tournament.status] || tournament.status}`
        + (editMode ? ' · редактирование' : ' · просмотр')
      : 'Турнир'
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return <Spinner text="Загрузка турнира..." />;
  if (pageError) return <PageError message={pageError} onRetry={fetchTournament} />;
  if (!tournament) return null;

  // SSE-алерт: рендерится поверх любого состояния страницы.
  // onConfirm вызывается при закрытии (например, принудительно закрыть ScoreModal).
  const SseAlertModal = sseAlert ? (
    <AlertModal
      icon={sseAlert.icon}
      title={sseAlert.title}
      message={sseAlert.message}
      onClose={() => {
        sseAlert.onConfirm?.();
        setSseAlert(null);
      }}
    />
  ) : null;

  const isDraft    = tournament.status === 'DRAFT';
  const minPlayers = (tournament.teamSize || 2) * 2;
  const canManage  = tournament.canManage !== false;

  // Для canStart используем локальное состояние участников (отражает незафиксированные изменения)
  const draftActiveCount = eventMembers.length > 0
    ? eventMembers.filter(m => draftIsActive(m.organizationMemberId)).length
    : (tournament.players || []).filter(p => p.status === 'ACTIVE').length;
  const canStart = draftActiveCount >= minPlayers;
  // seedingSupported / seedOrderItems / draftRandomOrder вычислены выше (черновое состояние посева).

  // Режим редактирования RUNNING-турнира — рендерим TournamentEditView вместо основного UI
  if (editMode) {
    return (
      <TournamentEditView
        tournament={tournament}
        clientId={clientId.current}
        organizationId={organizationId}
        onSaved={(updated) => {
          setTournament(updated);
          setEditMode(false);
        }}
        onRefresh={(updated) => setTournament(updated)}
        onCancel={() => setEditMode(false)}
      />
    );
  }

  // ── Общий Header ──────────────────────────────────────────────────────────

  const Header = () => (
    <div style={{
      background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
      padding: '14px 16px 16px', color: '#fff', flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => tournament?.eventPublicId
              ? navigate(`/events/${tournament.eventPublicId}`)
              : navigate(-1)
            }
            style={{
              background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 8, padding: '5px 10px', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center',
            }}
          >
            <IconArrowLeft />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconTrophy />
            </div>
            <div>
              <h1 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>
                {FORMAT_LABELS[tournament.format] || tournament.format}
              </h1>
              {tournament.sport && (
                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 1 }}>
                  {SPORT_LABELS[tournament.sport] || tournament.sport}
                </div>
              )}
            </div>
          </div>
        </div>
        <StatusBadge status={tournament.status} />
      </div>
    </div>
  );

  // ── DRAFT UI ───────────────────────────────────────────────────────────────

  if (isDraft) {
    return (
      <>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f8fafc' }}>
        <div style={{ position: 'sticky', top: 0, zIndex: 50 }}>
          <Header />
        </div>

        <div style={{ padding: '16px 16px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <DraftSettings
            courtsCount={draftCourts}           onCourtsCountChange={setDraftCourts}
            matchScoring={draftScoring}          onMatchScoringChange={setDraftScoring}
            matchDurationMinutes={draftDuration} onMatchDurationChange={setDraftDuration}
            rankingMode={draftRanking}           onRankingModeChange={setDraftRanking}
          />

          <TournamentMemberList
            members={eventMembers}
            loading={membersLoading}
            isActive={draftIsActive}
            onToggle={toggleMemberLocal}
            activeCount={draftActiveCount}
            minPlayers={minPlayers}
            seedingSupported={seedingSupported}
            randomOrder={draftRandomOrder}
            onRandomOrderChange={setDraftRandomOrder}
            orderItems={seedOrderItems}
            onReorder={handleReorderPlayers}
            eventId={tournament?.eventPublicId}
            organizationId={organizationId}
            onParticipantAdded={refetchEventMembers}
          />
        </div>

        {/* Нижняя панель.
            Не фиксируем к низу экрана: на мобильном при открытой клавиатуре
            (ввод кортов, редактирование списка участников) фиксированная панель
            съедала бы и без того малое полезное пространство. Кнопки — обычный
            блок в конце контента, пользователь докручивает до них сам. */}
        <div style={{
          padding: '12px 16px 24px',
          background: '#f8fafc',
          borderTop: '1px solid #f0f0f5',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {/* «Обновить черновик» появляется только при наличии изменений */}
          {isDirty && (
            <Button
              variant="ghost"
              fullWidth
              loading={updateLoading}
              disabled={startLoading}
              onClick={handleUpdateDraft}
              style={{ border: '1.5px solid #d1d5db' }}
            >
              Обновить черновик
            </Button>
          )}

          <Button
            variant="primary"
            fullWidth
            loading={startLoading}
            disabled={!canStart || updateLoading}
            onClick={handleStart}
            style={{
              background: canStart
                ? 'linear-gradient(135deg, #d97706, #b45309)'
                : '#e5e7eb',
              color: canStart ? '#fff' : '#6b7280',
              boxShadow: 'none',
            }}
          >
            {canStart
              ? `${isDirty ? 'Сохранить и запустить' : 'Старт'} · ${draftActiveCount} игроков`
              : `Нужно минимум ${minPlayers} игрока`}
          </Button>
        </div>
      </div>
      {SseAlertModal}
      </>
    );
  }

  // ── RUNNING / FINISHED / CANCELLED ────────────────────────────────────────

  const showTimer = tournament.matchScoring === 'TIME' && tournament.status === 'RUNNING';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f8fafc' }}>
      {/* Единый sticky-блок: шапка + таймер */}
      <div style={{ position: 'sticky', top: 0, zIndex: 50 }}>
        <Header />
        {showTimer && (
          <TournamentTimer
            tournament={tournament}
            canManage={canManage}
            onTournamentUpdate={handleTournamentUpdate}
            clientId={clientId.current}
          />
        )}
      </div>
      <RunningView
        tournament={tournament}
        canManage={canManage}
        sseLeaderboard={sseLeaderboard}
        onTournamentUpdate={handleTournamentUpdate}
        onEditTournament={() => setEditMode(true)}
        onMatchSaved={handleMatchSaved}
        onScoreModalOpen={handleScoreModalOpen}
        onScoreModalClose={handleScoreModalClose}
        clientId={clientId.current}
      />
      {SseAlertModal}
    </div>
  );
};

export default TournamentPage;
